import React from 'react';
import {PaginationButton, PaginationWrapper} from './styled';
import {Text} from '@phonectas/unifon-ui-kit';

interface PaginationProps {
    count: number;
    page: number;
    onChange: (event: React.MouseEvent<HTMLButtonElement>, page: number) => void;
    shape?: 'rounded' | 'square';
    variant?: 'outlined' | 'text';
}

const Pagination: React.FC<PaginationProps> = ({
	count,
	page,
	onChange,
	shape = 'rounded',
	variant = 'outlined',
}) => {
	if (count < 2) return null;

	const handleClick = (newPage: number) => (
		e: React.MouseEvent<HTMLButtonElement>
	) => {
		if (newPage !== page && newPage >= 1 && newPage <= count) {
			onChange(e, newPage);
		}
	};

	const getPageNumbers = () => {
		const pages: (number | string)[] = [];
        
		if (count <= 7) {
			for (let i = 1; i <= count; i++) {
				pages.push(i);
			}
		} else {
			pages.push(1);
            
			let start = Math.max(2, page - 2);
			let end = Math.min(count - 1, page + 2);
            
			if (end - start < 4) {
				if (start === 2) {
					end = Math.min(count - 1, start + 4);
				} else {
					start = Math.max(2, end - 4);
				}
			}
            
			if (start > 2) {
				pages.push('...');
			}
            

			for (let i = start; i <= end; i++) {
				pages.push(i);
			}
            
			if (end < count - 1) {
				pages.push('...');
			}
            
			if (count > 1) {
				pages.push(count);
			}
		}
        
		return pages;
	};

	const pageNumbers = getPageNumbers();

	return (
		<PaginationWrapper>
			<PaginationButton
				onClick={handleClick(page - 1)}
				disabled={page === 1}
				$shape={shape}
				$variant={variant}
				aria-label="Previous page"
			>
                &lt;
			</PaginationButton>

			{pageNumbers.map((pageNum, index) => {
				if (pageNum === '...') {
					return (
						<span key={`ellipsis-${index}`} style={{padding: '0 8px', color: '#666'}}>
                            ...
						</span>
					);
				}
                
				const pageNumber = pageNum as number;
				return (
					<PaginationButton
						key={pageNumber}
						onClick={handleClick(pageNumber)}
						$selected={page === pageNumber}
						$shape={shape}
						$variant={variant}
						aria-current={page === pageNumber ? 'page' : undefined}
					>
						<Text style={{color: page === pageNumber ? '#fff' : '#001B74'}}>
							{pageNumber}
						</Text>
					</PaginationButton>
				);
			})}
			<PaginationButton
				onClick={handleClick(page + 1)}
				disabled={page === count}
				$shape={shape}
				$variant={variant}
				aria-label="Next page"
			>
                &gt;
			</PaginationButton>
		</PaginationWrapper>
	);
};

export default Pagination;
